/**
 * The single source of wall-clock time.
 *
 * Everything in this project that asks "what time is it?" asks here, rather
 * than calling `performance.now()` directly. Two things depend on that:
 *
 *   1. **Headless trials.** `TrialRunner` runs hundreds of 60-second sessions
 *      in a couple of seconds by installing a virtual clock that advances one
 *      simulation tick at a time. The simulated participant's 80 ms response
 *      latency then costs 80 simulated milliseconds, not 80 real ones, and the
 *      results are identical to a realtime run.
 *
 *   2. **Determinism.** With a virtual clock, a trial's outcome depends only on
 *      its seed and its parameters -- not on how busy the machine was.
 *
 * Real hardware adapters (`SerialInput`, `WebSocketInput`, `KeyboardButtonDevice`)
 * deliberately keep using `performance.now()` directly: their events arrive from
 * the outside world and must be stamped on the real timeline no matter what the
 * simulation is doing.
 *
 * This is a mutable module-level binding, which is a compromise. Threading a
 * clock object through every constructor would touch twenty files and make the
 * common case harder to read. The rule that keeps it safe: only `TrialRunner`
 * swaps it, only around a synchronous block, and it always restores it.
 */
export interface Clock {
  now(): number;
}

export const realClock: Clock = {
  now: () => performance.now(),
};

/**
 * A clock the caller advances by hand. `advance()` moves time forward; nothing
 * else does.
 */
export class VirtualClock implements Clock {
  constructor(private t = 0) {}

  now(): number {
    return this.t;
  }

  advance(ms: number): number {
    this.t += ms;
    return this.t;
  }

  set(ms: number): void {
    this.t = ms;
  }
}

let active: Clock = realClock;

/** Current wall-clock reading, ms. */
export function now(): number {
  return active.now();
}

export function getClock(): Clock {
  return active;
}

/** Install a clock and return a function that restores the previous one. */
export function setClock(clock: Clock): () => void {
  const previous = active;
  active = clock;
  return () => {
    active = previous;
  };
}

/**
 * Run `fn` with `clock` installed, restoring the previous clock afterwards even
 * if `fn` throws. `fn` must be synchronous -- an await would let the real
 * application run a frame against the virtual clock.
 */
export function withClock<T>(clock: Clock, fn: () => T): T {
  const restore = setClock(clock);
  try {
    return fn();
  } finally {
    restore();
  }
}
